import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Paper,
  Box,
  Container,
  Toolbar,
  Chip,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import PublicIcon from '@mui/icons-material/Public';
import CategoryIcon from '@mui/icons-material/Category';
import LinkIcon from '@mui/icons-material/Link';
import AppBarPublico from '../../componentes/navegacion/AppBarPublico';
import { getNormativaBandejaById } from '../../componentes/http/actions/BandejaNormativaAction';

// ─── Sección reutilizable ────────────────────────────────────────────────────
const SeccionDetalle = ({ titulo, icono, children }) => (
  <Paper sx={{ p: 3, mb: 3 }}>
    <Box display="flex" alignItems="center" gap={1} mb={2}>
      {icono}
      <Typography variant="h6" fontWeight="bold">
        {titulo}
      </Typography>
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

// ─── Fila de dato simple ─────────────────────────────────────────────────────
const FilaDato = ({ label, valor }) => (
  <Box display="flex" gap={1} mb={1}>
    <Typography variant="body2" fontWeight="bold" minWidth={140} color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2">{valor || '—'}</Typography>
  </Box>
);

// ─── Componente principal ────────────────────────────────────────────────────
const DetalleNormativaPublico = () => {
  const { id } = useParams();
  const [normativa, setNormativa] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });

  const fetchNormativa = useCallback(() => {
    setIsLoading(true);
    getNormativaBandejaById(id, null)
      .then((data) => setNormativa(data))
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Error al cargar el detalle de la normativa',
          severity: 'error',
        })
      )
      .finally(() => setIsLoading(false));
  }, [id]);

  useEffect(() => {
    if (id) fetchNormativa();
  }, [fetchNormativa, id]);

  const formatFecha = (fecha) => {
    if (!fecha) return '—';
    return new Date(fecha).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <>
      <AppBarPublico />
      <Toolbar />

      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>

          {/* ── Loading ── */}
          {isLoading && (
            <Box display="flex" justifyContent="center" mt={6}>
              <CircularProgress />
            </Box>
          )}

          {/* ── Contenido ── */}
          {!isLoading && normativa && (
            <Grid container direction="column">

              {/* Encabezado */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {normativa.nombre}
                </Typography>

                {normativa.descripcion && (
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    {normativa.descripcion}
                  </Typography>
                )}
              </Paper>

              {/* Información general */}
              <SeccionDetalle
                titulo="Información General"
                icono={<DescriptionIcon color="primary" />}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FilaDato label="Regulador" valor={normativa.regulador} />
                    <FilaDato label="Tipo de Documento" valor={normativa.tipoDocumento} />
                    <FilaDato label="País" valor={normativa.pais} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FilaDato
                      label="Fecha"
                      valor={formatFecha(normativa.fecha)}
                    />
                    {normativa.linkSitioWeb && (
                      <Box display="flex" gap={1} mb={1}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          minWidth={140}
                          color="text.secondary"
                        >
                          Sitio Web:
                        </Typography>
                        <Link
                          href={normativa.linkSitioWeb}
                          target="_blank"
                          rel="noopener noreferrer"
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          variant="body2"
                        >
                          <LinkIcon fontSize="small" />
                          Ver fuente oficial
                        </Link>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </SeccionDetalle>

              {/* Rubros */}
              <SeccionDetalle
                titulo="Rubros"
                icono={<CategoryIcon color="primary" />}
              >
                {normativa.rubros?.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {normativa.rubros.map((rubro) => (
                      <Chip
                        key={rubro.id}
                        label={rubro.nombre}
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay rubros asociados.
                  </Typography>
                )}
              </SeccionDetalle>

              {/* Compromisos */}
              <SeccionDetalle
                titulo="Compromisos"
                icono={<GavelIcon color="primary" />}
              >
                {normativa.compromisos?.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: 60 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {normativa.compromisos.map((compromiso, index) => (
                        <TableRow key={compromiso.id} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{compromiso.descripcion}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay compromisos para esta normativa.
                  </Typography>
                )}
              </SeccionDetalle>

              {/* Obligaciones */}
              <SeccionDetalle
                titulo="Obligaciones"
                icono={<PublicIcon color="primary" />}
              >
                {normativa.obligaciones?.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: 60 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {normativa.obligaciones.map((obligacion, index) => (
                        <TableRow key={obligacion.id} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{obligacion.descripcion}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay obligaciones para esta normativa.
                  </Typography>
                )}
              </SeccionDetalle>

              {/* Documentos */}
              {normativa.documentos?.length > 0 && (
                <SeccionDetalle
                  titulo="Documentos"
                  icono={<DescriptionIcon color="primary" />}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Documento</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {normativa.documentos.map((doc, index) => (
                        <TableRow key={doc.id} hover>
                          <TableCell>
                            {doc.urlDocumento ? (
                              <Link
                                href={doc.urlDocumento}
                                target="_blank"
                                rel="noopener noreferrer"
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                              >
                                <LinkIcon fontSize="small" />
                                Documento {index + 1}
                              </Link>
                            ) : (
                              `Documento ${index + 1}`
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </SeccionDetalle>
              )}

            </Grid>
          )}

          {/* ── Sin resultados ── */}
          {!isLoading && !normativa && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No se encontró la normativa solicitada.
              </Typography>
            </Paper>
          )}

        </Box>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DetalleNormativaPublico;